/**
 * Copyright (c) 2017 Improove
 *
 * This file is part of Improove_Lux.
 *
 * Improove_Lux is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Improove_Lux is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Improove_Lux.  If not, see <http://www.gnu.org/licenses/>.
 */

/* jshint browser:true jquery:true */
/* global confirm:true */


'use strict';

var _ = require('lodash');

/**
 * State
 *
 * @constructor
 * @param {string} id - ID of a state
 * @param {Object} $containerEl - triggered element
 * @param {Object} $triggerEls - triggering element
 * @param {Object} options - used for callbacks
 * @return {this} - this
 */
var State = function (id, $containerEl, $triggerEls, options) {
    this.id = id;
    this.$containerEl = $containerEl;
    this.$triggerEls = $triggerEls;
    this.options = _.defaults(options, this.options);
    if (this.$containerEl[0].getAttribute('aria-hidden')) {
        this.options.aria = true;
    }
    if (this.options.aria &&
        this.$containerEl[0].getAttribute('aria-hidden')
    ) {
        this.$containerEl[0].setAttribute('aria-hidden', 'true');
    }

    return this;
};

State.prototype = {

    /** @type {?String} */
    id: null,

    /** @type {?jQuery} */
    $containerEl: null,

    /** @type {?jQuery} */
    $triggerEls: null,

    /** @type {Object} */
    options: {

        /** @type {?Function} */
        onActivate: null,

        /** @type {?Function} */
        onDeactivate: null,

        /**
         * Should we push a state to the browser-history when this state
         * is enabled/disabled?
         *
         * @type {Boolean}
         */
        history: false,

        /**
         * Set aria-hidden attributes for state container
         *
         * @type {Boolean}
         */
        aria: true },

    /**
     * Excecute callback on State Activation
     *
     * @return {this} - this
     */
    onActivate: function () {
        if (_.isFunction(this.options.onActivate)) {
            this.options.onActivate.call(this);
        }

        return this;
    },

    /**
     * Execute callback on State Deactivation
     * @param {boolean} transitioning - are we transitioning to another state?
     * @return {this} - this
     */
    onDeactivate: function (transitioning) {
        if (_.isFunction(this.options.onDeactivate)) {
            this.options.onDeactivate.call(this, transitioning);
        }

        return this;
    } };

/**
 * States is a singleton that handles the different layout states of the
 * app.
 *
 * @constructor
 * @return {this} - this
 */
var States = function () {
    this.$body = $('body');
    this.bindEvents();
    return this;
};

States.prototype = {

    /** @type {jQuery} */
    $body: null,

    /** @type {String} */
    bodyClassPrefix: 'state--',

    /** @type {String} */
    eventNamePrefix: 'states:',

    /** @type {Object} */
    states: {},

    /** @type {?Object} - Currently Active State */
    activeState: null,

    /** @type {?Function} */
    _boundTemp: null,

    /**
     * Bind Events
     *
     * @return {this} - this
     */
    bindEvents: function () {
        $(window).on(
            'hashchange popstate',
            this.activateFromHash.bind(this)
        );
        $(document).on('keyup', function (event) {
            if (event.keyCode === 27) {
                this.deactivateCurrent();
            }
        }.bind(this));
        return this;
    },


    /**
     * Activate from hash
     *
     * @return {this} - this
     */
    activateFromHash: function () {
        var hash = location.hash.slice(1);

        if (_.has(this.states, hash) &&
            (_.isNull(this.activeState) || this.activeState.id !== hash)) {
            this.activate(hash);
        } else if (_.isEmpty(hash) && !_.isNull(this.activeState)) {
            this.deactivate(this.activeState.id, false, true);
        }

        return this;
    },

    /**
     * Set location hash
     *
     * @param {string} id - id of a state
     * @param {boolean} push - should we push the state or not?
     * @return {this} - this
     */
    setHash: function (id, push) {
        var hash = location.hash.slice(1);
        var historyApi;

        if (hash !== id) {
            if (history.pushState) {
                if (push) {
                    historyApi = history.pushState.bind(history);
                } else {
                    historyApi = history.replaceState.bind(history);
                }
                historyApi(
                    null,
                    document.title,
                    window.location.pathname + window.location.search +
                    '#' + id
                );
            } else {
                location.hash = id;
            }
        }

        return this;
    },

    /**
     * Clear location hash
     *
     * @param {string} id - is of a state
     * @param {boolean} push - should we push the state or not?
     * @return {this} - this
     */
    clearHash: function (id, push) {
        var hash = location.hash.slice(1);
        var historyApi;

        if (hash === id) {
            if (history.pushState) {
                if (push) {
                    historyApi = history.pushState.bind(history);
                } else {
                    historyApi = history.replaceState.bind(history);
                }
                historyApi(
                    null,
                    document.title,
                    window.location.pathname + window.location.search
                );
            } else {
                location.hash = '';
            }
        }

        return this;
    },

    /**
     * Add a new state.
     * Intended usage is within a component that requires a layout-state
     *
     * @example
     * define([
     *     'jquery',
     *     'improove/states'
     * ], function($, states) {
     *     'use strict';
     *
     *     states.add('menu', $('.menu__container'), $('.menu__toggle'), {
     *         onActivate: function() {
     *             // callback for activate ...
     *         },
     *         onDeactivate: function() {
     *             // callback for deactivate ...
     *         }
     *     });
     * });
     *
     * @param {string} id - id of a state
     * @param {jQuery} $containerEl - element that is being triggered in state
     * @param {jQuery} $triggerEls - the triggering element
     * @param {Object} options - used for callbacks
     * @param {?Function} options.onActivate - callback for state activation
     * @param {?Function} options.onDeactivate - callback for state deactivation
     * @return {this} - this
     */
    add: function (id, $containerEl, $triggerEls, options) {
        if (id && $containerEl.length) {
            this.states[id] = new State(id, $containerEl, $triggerEls, options);
            this.bindStateTriggers(this.states[id]);
            this.activateFromHash();
        }

        return this;
    },

    /**
    * @param {string} id - ID of state
    * @param {jQuery} $containerEl - element that is being triggered in state
    * @param {jQuery} $triggerEls - the triggering element
    * @return {this} - this
    */
    updateDOMReferences: function (id, $containerEl, $triggerEls) {
        if (id && $containerEl.length) {
            var state = this.states[id];
            state.$containerEl = $containerEl;
            state.$triggerEls = $triggerEls;
            this.bindStateTriggers(this.states[id]);
            this.activateFromHash();
        }

        return this;
    },

    /**
     * Bind state triggers for given state
     *
     * @param {Object} state - state object
     * @return {this} - this
     */
    bindStateTriggers: function (state) {
        if (!_.isNull(state.$triggerEls)) {
            state.$triggerEls.on('click', this.toggle.bind(this, state.id));
        }

        return this;
    },

    /**
     * Unbind state triggers for given state
     *
     * @param {Object} state - state object
     * @return {this} - this
     */
    unbindStateTriggers: function (state) {
        if (!_.isNull(state.$triggerEls)) {
            state.$triggerEls.off(
                'click', this.toggle.bind(this, state.id));
        }

        return this;
    },

    /**
     * Remove a state by id
     *
     * @param {string} id - id of a state
     * @return {this} - this
     */
    remove: function (id) {
        if (_.has(this.states, id)) {
            this.unbindStateTriggers(this.states[id]);
            this.states = _.omit(this.states, id);
        }

        return this;
    },

    /**
     * Activate a state by id
     * @param {string} id - id of a state
     * @param {boolean} ignoreCallbacks - should state callbacks be ignored?
     * @return {this} - this
     */
    activate: function (id, ignoreCallbacks) {
        if (!_.isNull(this.activeState)) {
            this.deactivate(this.activeState.id, true, ignoreCallbacks);
        }
        if (_.has(this.states, id) && _.isNull(this.activeState)) {
            window.requestAnimationFrame(function () {
                this.$body[0].classList.add(this.bodyClassPrefix + id);
            }.bind(this));
            this.activeState = this.states[id];
            this.setHash(id, this.states[id].options.history);
            this.bindTemp(id);
            if (this.states[id].options.aria) {
                this.states[id].$containerEl[0].setAttribute(
                    'aria-hidden',
                    'false'
                );
                this.states[id].$triggerEls[0].setAttribute(
                    'aria-expanded',
                    'true'
                );
            }
            if (!ignoreCallbacks) {
                this.activeState.onActivate.call(this.states[id]);
            }
            $(document).trigger(this.eventNamePrefix + id + '-activated');
        }

        return this;
    },

    /**
     * Deactivate a state by id
     * @param {string} id - ID of a space
     * @param {boolean} transitioning - are we transitioning from state x to y?
     * @param {boolean} ignoreCallbacks - should state callbacks be ignored?
     * @return {this} - this
     */
    deactivate: function (id, transitioning, ignoreCallbacks) {
        if (_.has(this.states, id)) {
            window.requestAnimationFrame(function () {
                this.$body[0].classList.remove(this.bodyClassPrefix + id);
            }.bind(this));
            if (_.isEqual(this.activeState, this.states[id])) {
                this.activeState = null;
            }
            this.clearHash(id, this.states[id].options.history);
            this.unbindTemp();
            if (this.states[id].options.aria) {
                this.states[id].$containerEl[0].setAttribute(
                    'aria-hidden',
                    'true'
                );
                this.states[id].$triggerEls[0].setAttribute(
                    'aria-expanded',
                    'false'
                );
            }
            if (!ignoreCallbacks) {
                this.states[id].onDeactivate.call(
                    this.states[id], transitioning
                );
            }
            $(document).trigger(this.eventNamePrefix + id + '-deactivated');
        }
        return this;
    },

    /**
     * Toggle a state by id
     * @param {string} id - ID of a state
     * @param {jQuery.Event} event - JQuery event
     * @return {this} - this
     */
    toggle: function (id, event) {
        event.stopPropagation(); // dont propagate to body click temp bind
        if (_.isEqual(this.activeState, this.states[id])) {
            this.deactivate(id);
        } else {
            this.activate(id);
        }
        return this;
    },

    /**
     * Deactivate currently active state
     * @param {boolean} ignoreCallbacks - ignore or not
     * @return {this} - this
     */
    deactivateCurrent: function (ignoreCallbacks) {
        if (!_.isNull(this.activeState)) {
            this.deactivate(this.activeState.id, false, ignoreCallbacks);
        }

        return this;
    },

    /**
     * Retrieve state by id
     * @param {string} id - ID of a state
     * @return {false|Object} - Returns false if no state is found
     */
    getStateById: function (id) {
        if (_.has(this.states, id)) {
            return this.states[id];
        }

        return false;
    },

    /**
     * Bind temp click observer
     * @param {string} id - ID of a state
     */
    bindTemp: function (id) {
        this.unbindTemp();
        this.boundTemp = this.bodyClick.bind(this, id);
        this.$body.on('click', this.boundTemp);
    },

    /**
     * Unbind temp click observer
     */
    unbindTemp: function () {
        if (_.isFunction(this.boundTemp)) {
            this.$body.off('click', this.boundTemp);
        }
    },

    /**
     * Close current state if click was outside container
     * @param {id} id - ID of a state
     * @param {jQuery} event - event object
     */
    bodyClick: function (id, event) {
        if (this.states[id] &&
            (!_.isEqual(this.states[id].$containerEl[0], event.target) &&
             !$.contains(this.states[id].$containerEl[0], event.target))
        ) {
            this.deactivate(id);
            this.unbindTemp();
        }
    } };

/**
 * Create instance of States
 *
 * @type {States}
 */
module.exports = new States();
